import React, { useState } from 'react';
import { Button, Input, Space, Typography } from 'antd';
import firebase from 'firebase/app';

import './ClientEmailLogin.css';

const { Text } = Typography;

// Same site key used by the identity-first screen (invisible reCAPTCHA v3).
const GOOG_RECAPTCHA_SITE_KEY = '6LdZWSYrAAAAAIeT9OanpWKafZysLd_gMis6EgD8';

const inputStyle = {
  backgroundColor: '#1a1a1a',
  color: '#fff',
  borderColor: '#444',
};

const pinkButton = { backgroundColor: '#ff0098', borderColor: '#ff0098' };

function friendlyAuthError(err) {
  const code = err && err.code;
  switch (code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return 'Incorrect email or password.';
    case 'auth/user-not-found':
      return 'No account found for that email.';
    case 'auth/invalid-email':
      return 'That email address looks invalid.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/email-already-in-use':
      return 'That email already has an account — try signing in instead.';
    case 'auth/weak-password':
      return 'Choose a password of at least 6 characters.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    default:
      return (err && err.message) || 'Something went wrong. Please try again.';
  }
}

async function getRecaptchaToken(action) {
  try {
    await new Promise((resolve) => (
      window.grecaptcha?.ready ? window.grecaptcha.ready(resolve) : resolve()
    ));
    if (window.grecaptcha?.execute) {
      return await window.grecaptcha.execute(GOOG_RECAPTCHA_SITE_KEY, { action });
    }
  } catch (err) {
    console.warn('[ClientEmailLogin] reCAPTCHA unavailable:', err && err.message);
  }
  return null;
}

/**
 * Custom email/password sign-in + sign-up (replaces the FirebaseUI email flow).
 *
 * Key wins over FirebaseUI:
 *  - the email is PRE-FILLED from the identity-first lookup (no re-typing), and
 *  - "Forgot password?" runs OUR SendGrid one-time-code reset (passwordResetHandler),
 *    not Firebase's native reset-link email.
 *
 * On a successful sign-in / sign-up it calls handler(authResult, null) — the same
 * callback FirebaseUI used — so the anti-duplicate abort + human-verify still run.
 */
export default function ClientEmailLogin({ initialEmail = '', isSignup = false, handler }) {
  // 'login' | 'signup' | 'forgot'
  const [mode, setMode] = useState(isSignup ? 'signup' : 'login');
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Forgot-password sub-flow: 'email' | 'code' | 'password'
  const [forgotStep, setForgotStep] = useState('email');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const resetFn = (fnName) => firebase.functions()
    .httpsCallable(`userService-passwordResetHandler-${fnName}`);

  const trimmedEmail = () => email.trim().toLowerCase();

  const handleSignIn = async () => {
    if (!trimmedEmail() || !password) return;
    setError(null);
    setLoading(true);
    try {
      const cred = await firebase.auth().signInWithEmailAndPassword(trimmedEmail(), password);
      if (handler) handler(cred, null);
    } catch (err) {
      setError(friendlyAuthError(err));
      setLoading(false);
    }
    // On success we leave loading=true — the auth state change drives the next screen.
  };

  const handleCreate = async () => {
    if (!trimmedEmail() || password.length < 6 || !name.trim()) return;
    setError(null);
    setLoading(true);
    try {
      const cred = await firebase.auth().createUserWithEmailAndPassword(trimmedEmail(), password);
      if (cred.user) await cred.user.updateProfile({ displayName: name.trim() });
      if (handler) handler(cred, null);
    } catch (err) {
      setError(friendlyAuthError(err));
      setLoading(false);
    }
  };

  const openForgot = () => {
    setError(null);
    setNotice(null);
    setForgotStep('email');
    setResetCode('');
    setNewPassword('');
    setMode('forgot');
  };

  const backToLogin = (msg) => {
    setError(null);
    setNotice(msg || null);
    setPassword('');
    setMode('login');
  };

  const handleSendResetCode = async () => {
    if (!trimmedEmail()) return;
    setError(null);
    setLoading(true);
    try {
      const token = await getRecaptchaToken('password_reset');
      // Anti-enumeration: the backend always returns success, so we always advance.
      await resetFn('requestPasswordResetCode')({ email: trimmedEmail(), token, action: 'password_reset' });
      setForgotStep('code');
      setNotice('If an account exists for that email, we’ve sent a 6-digit code.');
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyResetCode = async () => {
    if (resetCode.trim().length !== 6) return;
    setError(null);
    setLoading(true);
    try {
      await resetFn('verifyPasswordResetCode')({ email: trimmedEmail(), code: resetCode.trim() });
      setForgotStep('password');
      setNotice(null);
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async () => {
    if (newPassword.length < 6) return;
    setError(null);
    setLoading(true);
    let resetResult;
    try {
      resetResult = await resetFn('confirmPasswordReset')({ email: trimmedEmail(), newPassword });
    } catch (err) {
      setError(friendlyAuthError(err));
      setLoading(false);
      return;
    }
    // Reset succeeded — sign the user straight in so they land on the dashboard (or their
    // claim/join-team redirect, resolved by the post-login decision tree). Prefer the custom
    // token from the backend (signs into the right uid regardless of which of the account's
    // emails was typed); fall back to email+password.
    try {
      const token = resetResult && resetResult.data && resetResult.data.token;
      const cred = token
        ? await firebase.auth().signInWithCustomToken(token)
        : await firebase.auth().signInWithEmailAndPassword(trimmedEmail(), newPassword);
      if (handler) handler(cred, null);
      // Leave loading=true: the auth-state change drives the redirect.
    } catch (err) {
      backToLogin('Password updated — please sign in with your new password.');
      setLoading(false);
    }
  };

  const emailField = (
    <Input
      type="email"
      placeholder="name@email.com"
      value={email}
      onChange={(e) => { setEmail(e.target.value); setError(null); }}
      size="large"
      style={inputStyle}
      autoComplete="username"
      disabled={loading}
    />
  );

  const errorNode = error && (
    <Text type="danger" style={{ fontSize: 12, display: 'block' }}>{error}</Text>
  );
  const noticeNode = notice && (
    <Text style={{ color: '#7CFC98', fontSize: 12, display: 'block' }}>{notice}</Text>
  );

  // ── Forgot-password sub-flow ──
  if (mode === 'forgot') {
    return (
      <div style={{ padding: '0 24px' }}>
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          <Text style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>Reset your password</Text>

          {forgotStep === 'email' && (
            <>
              <Text style={{ color: '#bbb', fontSize: 13 }}>
                Enter your email and we’ll send you a 6-digit code.
              </Text>
              {emailField}
              <Button type="primary" block size="large" style={pinkButton}
                loading={loading} disabled={loading || !email.trim()} onClick={handleSendResetCode}>
                Send reset code
              </Button>
            </>
          )}

          {forgotStep === 'code' && (
            <>
              <Input
                placeholder="123456"
                value={resetCode}
                onChange={(e) => { setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(null); }}
                size="large"
                style={inputStyle}
                inputMode="numeric"
                disabled={loading}
              />
              <Button type="primary" block size="large" style={pinkButton}
                loading={loading} disabled={loading || resetCode.trim().length !== 6} onClick={handleVerifyResetCode}>
                Verify code
              </Button>
            </>
          )}

          {forgotStep === 'password' && (
            <>
              <Text style={{ color: '#bbb', fontSize: 13 }}>Choose a new password (at least 6 characters).</Text>
              <Input.Password
                className="st-dark-pw"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(null); }}
                size="large"
                style={inputStyle}
                disabled={loading}
              />
              <Button type="primary" block size="large" style={pinkButton}
                loading={loading} disabled={loading || newPassword.length < 6} onClick={handleConfirmReset}>
                Update password
              </Button>
            </>
          )}

          {errorNode}
          {noticeNode}

          <Button type="text" block onClick={() => backToLogin()} disabled={loading}
            style={{ color: '#888', fontSize: 13 }}>
            ← Back to sign in
          </Button>
        </Space>
      </div>
    );
  }

  // ── Sign in / Sign up ──
  const signup = mode === 'signup';
  return (
    <div style={{ padding: '0 24px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <Text style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>
          {signup ? 'Sign up with email' : 'Sign in with email'}
        </Text>

        {emailField}

        {signup && (
          <Input
            placeholder="Your name"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(null); }}
            size="large"
            style={inputStyle}
            autoComplete="name"
            disabled={loading}
          />
        )}

        <Input.Password
          className="st-dark-pw"
          placeholder="Password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(null); }}
          onPressEnter={signup ? handleCreate : handleSignIn}
          size="large"
          style={inputStyle}
          autoComplete={signup ? 'new-password' : 'current-password'}
          disabled={loading}
        />

        <Button
          type="primary"
          block
          size="large"
          style={pinkButton}
          loading={loading}
          disabled={loading || !email.trim() || !password || (signup && (!name.trim() || password.length < 6))}
          onClick={signup ? handleCreate : handleSignIn}
        >
          {signup ? 'Create account' : 'Sign in'}
        </Button>

        {errorNode}
        {noticeNode}

        {!signup && (
          <Button type="text" block onClick={openForgot} disabled={loading}
            style={{ color: '#888', fontSize: 13 }}>
            Forgot password?
          </Button>
        )}
      </Space>
    </div>
  );
}
